import { useEffect, useRef } from "react";
import type { Consumer, PhoneVerification } from "features/consumers/types";
import { getConsumerByEmailOrId } from "features/consumers/api";
import { useSimpleRequest } from "hooks/useRequest";
import type { ID } from "types";
import { compareIds } from "util/support";
import { TabIndex, TabProps } from "../types";

const prevConsumerCoreIdSymbol = Symbol('prevConsumerCoreId');

interface UsePhoneVerification {
    (props: TabProps<TabIndex.PhoneVerification>): void;
    [prevConsumerCoreIdSymbol]?: ID;
}

const pickPhoneVerification = ({
    phone,
    phoneVerified,
    verificationPhone,
    verificationPhoneType,
    verificationResponse,
    verificationPhoneResponse,
    verificationIncrement,
    verificationCreatedAt,
    verificationUpdatedAt,
    failedVerificationPhone,
    failedVerificationResponse,
    failedVerificationPhoneResponse,
    failedVerificationIncrement,
    failedVerificationCreatedAt,
    failedVerificationUpdatedAt
}: Consumer): PhoneVerification => ({
    phone,
    phoneVerified,
    verificationPhone,
    verificationPhoneType,
    verificationResponse,
    verificationPhoneResponse,
    verificationIncrement,
    verificationCreatedAt,
    verificationUpdatedAt,
    failedVerificationPhone,
    failedVerificationResponse,
    failedVerificationPhoneResponse,
    failedVerificationIncrement,
    failedVerificationCreatedAt,
    failedVerificationUpdatedAt
});

const usePhoneVerification: UsePhoneVerification = ({
    getTabPayload,
    dialogRequestProcessing: [
        isLoading,
        setLoading
    ],
    tabRegistry: [, updateDialogDataRegistry]
}) => {
    const request = useSimpleRequest();
    const { coreId } = getTabPayload<Consumer>();

    const helpers = {
        request,
        setLoading,
        getTabPayload,
        updateDialogDataRegistry
    };

    const helpersRef = useRef(helpers);
    helpersRef.current = helpers;

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const {
            request,
            setLoading,
            getTabPayload,
            updateDialogDataRegistry
        } = helpersRef.current;

        if (!compareIds(coreId, usePhoneVerification[prevConsumerCoreIdSymbol]!)) {
            setLoading(true);

            request<Consumer>(() => getConsumerByEmailOrId(getTabPayload<Consumer>()), {
                onSuccess: ({ data }) => updateDialogDataRegistry(pickPhoneVerification(data))
            })
                .finally(() => setLoading(false));
        }

        usePhoneVerification[prevConsumerCoreIdSymbol] = coreId;
    }, [coreId, isLoading]);
};

usePhoneVerification[prevConsumerCoreIdSymbol] = '';

export default usePhoneVerification;
